import { Navigate, Outlet } from 'react-router-dom'

import { hasRole } from '@/lib/roles'

import { useAuthStore } from '@/stores/auth'

import { paths } from '@/config/paths'

import type { UserRole } from '@/types/user-role'

interface PermissionProtectedRouteProps {
  requiredRoles?: UserRole[]
  requiredPermissions?: string[]
  requireAll?: boolean
  redirectTo?: string
}

/**
 * Route-level permission guard for nested routes
 * Redirects to unauthorized page if user doesn't have required permissions
 *
 * Use this in router configuration to protect entire route groups
 *
 * @example
 * {
 *   element: <PermissionProtectedRoute requiredRoles={['super_admin', 'gym_manager']} />,
 *   children: [
 *     { path: 'create', element: <CreatePage /> },
 *     { path: ':id/edit', element: <EditPage /> }
 *   ]
 * }
 */
export default function PermissionProtectedRoute({
  requiredRoles = [],
  requiredPermissions = [],
  requireAll = false,
  redirectTo = paths.public.unauthorized.path,
}: PermissionProtectedRouteProps) {
  const { user } = useAuthStore()

  if (!user) {
    return <Navigate to={paths.public.auth.login.path} replace />
  }

  // Check roles
  const hasRequiredRole =
    requiredRoles.length === 0 ||
    (requireAll
      ? requiredRoles.every((role) => hasRole(user, role))
      : requiredRoles.some((role) => hasRole(user, role)))

  // Check permissions
  const userPermissions = user.permissions ?? []
  const hasRequiredPermission =
    requiredPermissions.length === 0 ||
    (requireAll
      ? requiredPermissions.every((perm) => userPermissions.includes(perm))
      : requiredPermissions.some((perm) => userPermissions.includes(perm)))

  if (!hasRequiredRole || !hasRequiredPermission) {
    return <Navigate to={redirectTo} replace />
  }

  return <Outlet />
}
